// The naming diagram of WARDROOM · FLEET NAMES (monetization.md §3.2,
// R2186): the companion column shows how a world names a hull, as one
// example name in large type with a line from each of its four parts to a
// label.
//
// The rule is the server's (`server/internal/sim/names.go`, R44, R1249):
// the faction letter, then SN for a combat hull or SV for a support hull,
// then a name of the pool, then a Roman ordinal from II once a hull of
// this season already has the name. The pool is the account's fleet name
// pool when it holds a name, else the faction pool. The diagram mirrors no
// rule: it draws the letter of `HULL_POOLS` (names.toml) and the names the
// form drafts, so it follows each keystroke.
//
// The diagram is a grid. Row 1 holds the four parts, each over a bracket;
// under it a stem runs down from each part to its label, the rightmost
// part on the top row, so no stem crosses a label.

import { HULL_POOLS } from '../../../gen/balance';
import { hubWardroom as S } from '../../../gen/strings';
import { factionColor, hex } from '../../../world';
import { esc } from '../panel';

/** The example the diagram draws. */
export interface NameExample {
  /** The faction letter of the reader. */
  letter: string;
  /** The name part of the example. */
  name: string;
  /** The name part of the support example. */
  second: string;
  /** True when the name comes from the account's own pool. */
  own: boolean;
}

/**
 * The example for faction `faction` (0 for none: the first faction) and
 * the pool `names`: the first name of the pool, else the first name of the
 * faction pool. The support example takes the second name of the pool
 * where one stands.
 */
export function nameExample(faction: number, names: readonly string[]): NameExample {
  const row = HULL_POOLS.find((r) => r.faction === faction) ?? HULL_POOLS[0];
  const own = names.length > 0;
  const name = own ? names[0] : row.first;
  return { letter: row.letter, name, second: own ? names[1] ?? name : name, own };
}

/** One part of the example: its column, its word and its two label lines. */
interface Part { cls: string; word: string; label: string; sub: string }

/**
 * The markup of the diagram. `short` is the form of a fold: the parts and
 * one-line labels, with no example lines and no sentence, so a telephone
 * reads it over the form in a few lines.
 */
export function namingDiagram(faction: number, names: readonly string[], short = false): string {
  const ex = nameExample(faction, names);
  const parts: Part[] = [
    { cls: 'fac', word: ex.letter, label: S.dg_faction, sub: S.dg_faction_sub },
    { cls: 'role', word: S.dg_combat, label: S.dg_role, sub: S.dg_role_sub },
    { cls: 'nm', word: ex.name, label: S.dg_name, sub: ex.own ? S.dg_name_pool : S.dg_name_faction },
    { cls: 'ord', word: S.dg_second, label: S.dg_ordinal, sub: S.dg_ordinal_sub },
  ];
  const ink = hex(factionColor(faction || 1));
  const n = parts.length;
  // The label of part i stands on row n + 1 - i, so the rightmost label
  // is the top one; its stem runs from row 2 down to that row.
  const words = parts.map((p, i) => `<b class="fd-dw ${p.cls}" style="grid-column:${i + 1}`
    + `${p.cls === 'fac' ? `;--fd-ink:${ink}` : ''}">${esc(p.word)}</b>`).join('');
  const leads = parts.map((p, i) => {
    const row = n + 1 - i;
    const stem = row > 2
      ? `<span class="fd-ds ${p.cls}" aria-hidden="true" style="grid-column:${i + 1};grid-row:2 / ${row}"></span>` : '';
    const sub = short ? '' : `<small>${esc(p.sub)}</small>`;
    return stem + `<span class="fd-dl ${p.cls}" style="grid-column:${i + 1} / -1;grid-row:${row}">`
      + `<i>${esc(p.label)}</i>${sub}</span>`;
  }).join('');
  const grid = `<div class="fd-dg" role="group" aria-label="${esc(S.names_side_label)}">${words}${leads}</div>`;
  if (short) return grid;
  const line = (name: string, note: string): string =>
    `<div class="fd-dx"><b>${esc(name)}</b><i>${esc(note)}</i></div>`;
  return grid
    + line(`${ex.letter}${S.dg_support} ${ex.second}`, S.dg_sv_note)
    + line(`${ex.letter}${S.dg_combat} ${ex.name} ${S.dg_second}`, S.dg_ii_note)
    + `<p class="fd-dd">${esc(ex.own ? S.dg_draw_pool : S.dg_draw_faction)}</p>`;
}
